import { ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import {
  DxDateBoxModule,
  DxNumberBoxModule,
  DxSelectBoxModule,
  DxTagBoxModule,
  DxTextAreaModule,
  DxTextBoxModule,
  DxValidationGroupComponent,
  DxValidationGroupModule,
  DxValidatorModule,
} from 'devextreme-angular';
import { AppGlobals } from 'src/app/app.globals';
import { ContactService } from 'src/app/services/contact.service';
import { ContractService } from 'src/app/services/contracts/contract.service';
import { GridConnectionService } from 'src/app/services/grid-connection.service';
import { HasChanges } from 'src/app/services/guards/unsaved-changes.guard';
import { GpdButtonComponent } from 'src/app/components/gpd-button/gpd-button.component';
import { GpdCheckboxComponent } from 'src/app/components/gpd-checkbox/gpd-checkbox.component';
import { GpdPillComponent } from 'src/app/components/gpd-pill/gpd-pill.component';
import { SvgIconComponent } from 'src/app/components/svg-icon/svg-icon.component';
import { FormFooterComponent } from 'src/app/components/form-footer/form-footer.component';
import { ValidationToastComponent } from 'src/app/components/validation-toast/validation-toast.component';
import { ContactsDropdownBoxComponent } from 'src/app/components/contacts-dropdown-box/contacts-dropdown-box.component';
import { ContactsPopupComponent } from 'src/app/components/contacts-popup/contacts-popup.component';
import { FileViewerOverlayService } from 'src/app/components/file-viewer-overlay/file-viewer-overlay.service';
import type { ContractType } from 'src/app/pages/contracts/contract-form/contract-form.component';
import {
  ARTICLES_OF_ASSOCIATION_TYPE_KEY,
  ContractFormPresetState,
} from 'src/app/pages/contracts/contract-form/contract-form-preset';
import { SocietyDraftService } from './society-draft.service';
import { ContractsPillComponent } from 'src/app/components/contracts-pill/contracts-pill.component';
import { ContractSelectorPopupComponent } from 'src/app/components/contract-selector-popup/contract-selector-popup.component';
import { FileViewerService } from 'src/app/services/file-viewer.service';
import {
  RepresentativeFunction,
  ShareholderRole,
  SocietyDocumentKind,
  SocietyItem,
  SocietySalesModel,
  SocietyService,
} from 'src/app/services/society.service';

/** A company or person picked with the standard contacts dropdown (shareholders: also a Gesellschaft). */
interface ContactRef {
  contactId: number | null;
  contactType: 'company' | 'person' | 'society' | null;
}

interface Representative extends ContactRef {
  function: RepresentativeFunction;
}

interface Shareholder extends ContactRef {
  role: ShareholderRole | null;
  share: number | null;
  /** Hafteinlage, Kommanditisten only. */
  liabilityAmount: number | null;
}

type LinkedList = 'projects' | 'contracts';

/** A file of the form: picked now (file) or already uploaded (saved). */
interface SocietyFile {
  kind: SocietyDocumentKind;
  name: string;
  file?: File;
  saved?: { id: number; uuid: string };
}

/** Select options: the key is stored, the text shown. */
interface KeyOption {
  key: string;
  text: string;
}

/**
 * "Gesellschaft erstellen" / editing a saved Gesellschaft (route contacts/society/:id) -
 * standalone page like "Vertrag hinzufügen".
 */
interface DocumentGroup {
  id: string;
  label: string;
  sections: { kind: SocietyDocumentKind; label: string; single?: boolean }[];
}

@Component({
    selector: 'app-contacts-add-society-page',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CommonModule,
        DxDateBoxModule,
        DxNumberBoxModule,
        DxSelectBoxModule,
        DxTagBoxModule,
        DxTextAreaModule,
        DxTextBoxModule,
        DxValidationGroupModule,
        DxValidatorModule,
        GpdButtonComponent,
        GpdCheckboxComponent,
        GpdPillComponent,
        SvgIconComponent,
        FormFooterComponent,
        ValidationToastComponent,
        ContactsDropdownBoxComponent,
        ContactsPopupComponent,
        ContractSelectorPopupComponent,
        ContractsPillComponent,
    ],
    templateUrl: './contacts-add-society.page.html',
    styleUrls: ['./contacts-add-society.page.css']
})
export class ContactsAddSocietyPageComponent implements OnInit, HasChanges {

  @ViewChild('societyValidationGroup') societyValidationGroup: DxValidationGroupComponent;
  @ViewChild('validationToast') validationToast: ValidationToastComponent;
  @ViewChild(ContactsPopupComponent) contactsPopup: ContactsPopupComponent;
  @ViewChild('projectsListEl') projectsListEl?: ElementRef<HTMLElement>;
  @ViewChild('contractsListEl') contractsListEl?: ElementRef<HTMLElement>;

  hasChanges: boolean = false;
  isSavingChanges: boolean = false;
  private initialFormData: string;

  /** 0 while creating; the id of the Gesellschaft being edited otherwise. */
  societyId = 0;

  // Proposed values, not confirmed by the business side yet. Exported for the list page.
  static readonly legalForms: KeyOption[] = [
    { key: 'gmbh', text: 'GmbH' },
    { key: 'gmbhCoKg', text: 'GmbH & Co. KG' },
    { key: 'ug', text: 'UG (haftungsbeschränkt)' },
    { key: 'ag', text: 'AG' },
    { key: 'kg', text: 'KG' },
  ];
  static readonly societyStates: KeyOption[] = [
    { key: 'founding', text: $localize`:@@societyStateFounding:In formation` },
    { key: 'active', text: $localize`:@@societyStateActive:Active` },
    { key: 'liquidation', text: $localize`:@@societyStateLiquidation:Liquidation/legal succession` },
  ];
  readonly legalForms = ContactsAddSocietyPageComponent.legalForms;
  readonly societyStates = ContactsAddSocietyPageComponent.societyStates;
  readonly transactionTypes: KeyOption[] = [
    { key: 'founding', text: $localize`:@@societyTransactionFounding:Founding` },
    { key: 'purchase', text: $localize`:@@societyTransactionPurchase:Purchase & transfer` },
  ];
  readonly managingDirectorRequired = $localize`:@@societyManagingDirectorRequired:At least one representative is required`;
  readonly representationRules: KeyOption[] = [
    { key: 'single', text: $localize`:@@societyRepresentationSingle:Sole representation` },
    { key: 'joint', text: $localize`:@@societyRepresentationJoint:Joint` },
    { key: 'combined', text: $localize`:@@societyRepresentationCombined:Combined sole and joint representation` },
  ];

  // Shareholders can be companies or persons: the standard contacts dropdown shows both.
  companies: any[];
  persons: any[];
  contactsPopupVisible: boolean = false;
  private popupTarget: ContactRef | null = null;
  projects: any[] = [];
  contracts: any[] = [];
  articlesType: ContractType | null = null;
  countries: any[] = [];

  formData = {
    legalName: null as string | null,
    legalForm: null as string | null,
    status: null as string | null,
    transactionType: null as string | null,
    foundingDate: null as Date | null,
    // Satzungssitz: registeredSeat is its city.
    registeredSeatAddress: null as string | null,
    registeredSeatZipcode: null as string | null,
    registeredSeat: null as string | null,
    registeredSeatCountryId: 2,
    // Geschäftsanschrift: only entered when it differs, otherwise saved as the Satzungssitz.
    businessAddressDiffers: false,
    address: null as string | null,
    zipcode: null as string | null,
    city: null as string | null,
    countryId: 2, // Germany, same default as the company form
    registerCourt: null as string | null,
    registerNumber: null as string | null,
    shareCapital: null as number | null,
    shareholders: [{ contactId: null, contactType: null, role: null, share: null, liabilityAmount: null }] as Shareholder[],
    // Persons or companies; one empty row to start with, like the shareholders.
    managingDirectors: [{ contactId: null, contactType: null, function: 'managing_director' }] as Representative[],
    purpose: null as string | null,
    representationRule: null as string | null,
    taxAdvisor: { contactId: null, contactType: null } as ContactRef,
    // Only asked for on "Kauf & Übertragung" (see isPurchase).
    previousTaxAdvisor: { contactId: null, contactType: null } as ContactRef,
    // Zuständiger Buchhalter and its Vertretung, both optional.
    accountant: { contactId: null, contactType: null } as ContactRef,
    accountantDeputy: { contactId: null, contactType: null } as ContactRef,
    // Assumption: "USt." means subject to VAT - meaning and label still to be confirmed.
    vatLiable: false,
    taxNumber: null as string | null,
    vatId: null as string | null,
    // Steuerliche Betreuung (in house / external): not in the form for now, a stored value is kept.
    taxCare: null as SocietyItem['taxCare'],
    salesModelId: null as number | null,
    projectIds: [] as number[],
    // The Gesellschaftsvertrag is a contract of the standard type "Gesellschaftsvertrag".
    articlesContractId: null as number | null,
    contractIds: [] as number[],
    documents: [] as SocietyFile[],
    /** Uploaded files the user removed; deleted on save. */
    removedDocumentIds: [] as number[],
    /** The saved UBO file a newly picked one replaces; see handleSubmit. */
    replacedUboId: null as number | null,
  };

  constructor(
    private appGlobals: AppGlobals,
    private contactService: ContactService,
    private contractService: ContractService,
    private gridConnectionService: GridConnectionService,
    private fileViewerOverlay: FileViewerOverlayService,
    private societyDraft: SocietyDraftService,
    private societyService: SocietyService,
    private fileViewerService: FileViewerService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private location: Location
  ) {
    this.appGlobals.SetGlobalMenuIndex(6);
  }

  ngOnInit(): void {
    this.societyId = Number(this.activatedRoute.snapshot.params['id']) || 0;
    // Snapshot before any await, see contacts-add-company.component.ts.
    this.initialFormData = this.serialize();
    if (!this.restoreDraft() && this.societyId) {
      this.loadSociety();
    }
    if (this.societyId) {
      this.loadOwnedSites();
    }
    this.loadAsyncData();
  }

  /**
   * Standorte where this Gesellschaft is Grundstückseigentümer, shown like on a contact's page
   * (site pill + "Grundstückseigentümer"). Read-only: the owner is changed on the Grundstücke page.
   */
  ownedSites: { siteId: number; siteNr: string; siteName: string; role: string }[] = [];

  private async loadOwnedSites(): Promise<void> {
    const result = await this.contactService
      .getContactProjects({ ContactProjectRequestItem: { contactId: this.societyId, contactType: 'society' } })
      .catch(() => null);
    const sites = new Map<number, { siteId: number; siteNr: string; siteName: string; role: string }>();
    for (const item of result?.contactProjectItems ?? []) {
      const route = (item.routingInfo ?? []).find((r: any) => r.descriptionRouteType === 'property');
      if (!route || item.siteId == null || sites.has(item.siteId)) continue;
      // "12345 | JP Test", split back into the pill's Nr and name.
      const [nr, ...name] = String(item.displayName ?? '').split(' | ');
      sites.set(item.siteId, { siteId: item.siteId, siteNr: nr, siteName: name.join(' | '), role: route.description });
    }
    this.ownedSites = [...sites.values()];
  }

  openOwnedSiteProperties(site: { siteId: number }, event?: MouseEvent): void {
    const url = `site/${site.siteId}/properties`;
    if (event?.ctrlKey || event?.metaKey) window.open(url);
    else this.router.navigate([url]);
  }

  // Back from "Vertrag hinzufügen" (saved or aborted): continue with what was entered before.
  private restoreDraft(): boolean {
    const draft = this.societyDraft.take();
    const createdContractId = this.contractService.consumeCreatedPresetContractId();
    // A draft left behind by another Gesellschaft (user left without coming back) is dropped.
    if (!draft || draft.societyId !== this.societyId) return false;
    // Over the defaults, so fields added since the draft was kept are there too.
    this.formData = { ...this.formData, ...draft.formData };
    this.initialFormData = draft.initialFormData;
    if (createdContractId) this.formData.articlesContractId = createdContractId;
    return true;
  }

  private async loadSociety(): Promise<void> {
    const result = await this.societyService.getSociety(this.societyId).catch(error => ({ hasError: true, feedback: error }));
    if (result?.hasError || !('society' in result) || !result.society) {
      AppGlobals.ShowErrorMessage(result?.feedback);
      return;
    }
    const s = result.society;
    const contact = (c: any): ContactRef => ({ contactId: c?.contactId ?? null, contactType: c?.contactType ?? null });
    this.formData = {
      ...this.formData,
      legalName: s.legalName,
      legalForm: s.legalForm,
      status: s.status,
      transactionType: s.transactionType,
      foundingDate: s.foundingDate ? new Date(s.foundingDate) : null,
      registeredSeatAddress: s.registeredSeatAddress ?? null,
      registeredSeatZipcode: s.registeredSeatZipcode ?? null,
      registeredSeat: s.registeredSeat,
      registeredSeatCountryId: s.registeredSeatCountryId ?? this.formData.registeredSeatCountryId,
      // Saved before the Satzungssitz had its own address: keep a business address that is there.
      businessAddressDiffers:
        s.businessAddressDiffers ||
        (!s.registeredSeatAddress && !s.registeredSeatZipcode && !!(s.address || s.zipcode || (s.city && s.city !== s.registeredSeat))),
      address: s.address,
      zipcode: s.zipcode,
      city: s.city,
      countryId: s.countryId ?? this.formData.countryId,
      registerCourt: s.registerCourt,
      registerNumber: s.registerNumber,
      shareCapital: s.shareCapital,
      shareholders: s.shareholders?.length
        ? s.shareholders.map(sh => ({ contactId: sh.contactId, contactType: sh.contactType, role: sh.role ?? null, share: sh.share, liabilityAmount: sh.liabilityAmount ?? null }))
        : [{ contactId: null, contactType: null, role: null, share: null, liabilityAmount: null }],
      managingDirectors: s.managingDirectors?.length
        ? s.managingDirectors.map(d => ({ contactId: d.contactId, contactType: d.contactType, function: d.function ?? 'managing_director' }))
        : [{ contactId: null, contactType: null, function: 'managing_director' }],
      purpose: s.purpose ?? null,
      // Free text saved before this was a dropdown doesn't match an option: it is asked for again.
      representationRule: this.representationRules.some(o => o.key === s.representationRule) ? s.representationRule : null,
      taxAdvisor: contact(s.taxAdvisor),
      previousTaxAdvisor: contact(s.previousTaxAdvisor),
      accountant: contact(s.accountant),
      accountantDeputy: contact(s.accountantDeputy),
      vatLiable: s.vatLiable,
      taxNumber: s.taxNumber ?? null,
      vatId: s.vatId ?? null,
      taxCare: s.taxCare ?? null,
      salesModelId: s.salesModelId ?? null,
      projectIds: s.projectIds ?? [],
      articlesContractId: s.articlesContractId,
      contractIds: s.contractIds ?? [],
      documents: (s.documents ?? []).map(d => ({ kind: d.kind, name: d.name, saved: { id: d.id, uuid: d.uuid } })),
      removedDocumentIds: [],
      replacedUboId: null,
    };
    this.initialFormData = this.serialize();
    this.updateOtherContracts();
    this.updateListsScrollable();
  }

  private async loadAsyncData(): Promise<void> {
    await this.loadContacts();

    this.contractService.projects$.subscribe(data => {
      this.projects = data ?? [];
      this.updateListsScrollable();
    });
    this.contractService.contracts$.subscribe(data => {
      this.contracts = data ?? [];
      this.updateOtherContracts();
      this.updateListsScrollable();
    });
    this.contractService.contractTypes$.subscribe(
      (types: ContractType[]) => (this.articlesType = types?.find(t => t.systemKey === ARTICLES_OF_ASSOCIATION_TYPE_KEY) ?? null)
    );
    this.contractService.getProjects({});
    this.contractService.getContracts({});
    this.contractService.getContractTypes({});

    const countryResult = await this.gridConnectionService.getCountries({});
    if (countryResult?.hasError === true) AppGlobals.ShowErrorMessage(countryResult.feedback);
    else this.countries = countryResult?.countryList ?? [];
  }

  private async loadContacts(): Promise<void> {
    const [companyResult, personResult, salesModelResult] = await Promise.all([
      this.contactService.getCompanies({}),
      this.contactService.getPersons({}),
      this.societyService.getSalesModels().catch(() => null),
    ]);
    this.salesModels = salesModelResult?.salesModels ?? [];
    this.companies = companyResult?.companyItems ?? [];
    this.persons = personResult?.personItems ?? [];
  }

  /** Verkaufsmodelle from Admin ▸ Gesellschaften. */
  salesModels: SocietySalesModel[] = [];

  // Komplementär / Kommanditist: drawn as a solid / dashed line in the org chart.
  readonly representativeFunctions: { key: RepresentativeFunction; text: string }[] = [
    { key: 'managing_director', text: $localize`:@@societyFunctionManagingDirector:Managing director` },
    { key: 'authorized_officer', text: $localize`:@@societyFunctionAuthorizedOfficer:Authorized officer` },
    { key: 'board_member', text: $localize`:@@societyFunctionBoardMember:Executive board` },
  ];

  readonly shareholderRoles: { key: ShareholderRole; text: string }[] = [
    { key: 'general_partner', text: $localize`:@@societyGeneralPartner:General partner` },
    { key: 'limited_partner', text: $localize`:@@societyLimitedPartner:Limited partner` },
  ];

  onContactSelected(e: any, target: ContactRef): void {
    target.contactId = e?.data?.id ?? null;
    target.contactType = e?.type ?? null;
  }

  // "Person/Firma erstellen" inside the dropdown opens the contacts popup, as on the contract form.
  onNewContactFromDropdown(type: string, target: ContactRef): void {
    this.popupTarget = target;
    if (this.contactsPopup) {
      this.contactsPopup.popupSelectedTab = type === 'person' ? 1 : 0;
      this.contactsPopup.editMode = true;
      this.contactsPopup.openedDirectlyInEditMode = true;
    }
    this.contactsPopupVisible = true;
  }

  async onPopupContactSelected(e: any): Promise<void> {
    if (this.popupTarget) {
      this.popupTarget.contactId = e.data ? e.data.id : e.id;
      this.popupTarget.contactType = e.type;
    }
    this.contactsPopupVisible = false;
    await this.loadContacts();
  }

  get isPurchase(): boolean {
    return this.formData.transactionType === 'purchase';
  }

  /** Stammkapital for GmbH and UG, Grundkapital for an AG (both required); "Capital" until a legal form is picked. */
  get capitalLabel(): string {
    if (this.formData.legalForm === 'ag') return $localize`:@@societyStockCapital:Share capital (AG)`;
    if (this.capitalRequired) return $localize`:@@societyShareCapital:Share capital`;
    return $localize`:@@societyCapital:Capital`;
  }

  get capitalRequired(): boolean {
    return ['gmbh', 'ug', 'ag'].includes(this.formData.legalForm ?? '');
  }

  /** Komplementär / Kommanditist only exist in a Kommanditgesellschaft (KG, GmbH & Co. KG). */
  get hasPartnerRoles(): boolean {
    return this.formData.legalForm === 'kg' || this.formData.legalForm === 'gmbhCoKg';
  }

  // New tab, so the unsaved form stays as it is.
  openContact(contact: ContactRef): void {
    window.open(`contacts/${contact.contactType}/${contact.contactId}`, '_blank');
  }

  addShareholder(): void {
    this.formData.shareholders.push({ contactId: null, contactType: null, role: null, share: null, liabilityAmount: null });
  }

  removeShareholder(index: number): void {
    this.formData.shareholders.splice(index, 1);
    if (this.formData.shareholders.length === 0) this.addShareholder();
  }

  addManagingDirector(): void {
    this.formData.managingDirectors.push({ contactId: null, contactType: null, function: 'managing_director' });
  }

  removeManagingDirector(index: number): void {
    this.formData.managingDirectors.splice(index, 1);
    if (this.formData.managingDirectors.length === 0) this.addManagingDirector();
  }

  /** A just-created contract may not be in the list yet; the pill then shows the type name. */
  get articlesContract(): any | null {
    const id = this.formData.articlesContractId;
    if (!id) return null;
    return this.contracts.find(c => c.id === id) ?? { id, designation: this.articlesType?.name ?? '' };
  }

  /**
   * The Gesellschaftsvertrag has its own field, so the other contracts list leaves it out.
   * A stored array, not a getter: a fresh array on every change detection makes the tag box
   * rebuild its list and swallow the checkbox clicks.
   */
  otherContracts: any[] = [];

  private updateOtherContracts(): void {
    this.otherContracts = this.contracts.filter(c => c.id !== this.formData.articlesContractId);
  }

  // Picked entries are listed below their picker, like "Verknüpfte Projekte" on a contact.
  get selectedProjects(): any[] {
    return this.projects.filter(p => this.formData.projectIds.includes(p.projectId));
  }

  get selectedContracts(): any[] {
    return this.contracts.filter(c => this.formData.contractIds.includes(c.id));
  }

  // Search + count as on a contact's related projects/contracts; the search opens from the
  // icon in the list's head row.
  searchTerm: Record<LinkedList, string> = { projects: '', contracts: '' };
  searchOpen: Record<LinkedList, boolean> = { projects: false, contracts: false };

  get filteredProjects(): any[] {
    return this.filterBy(this.selectedProjects, 'projects', p => p.displayName);
  }

  get filteredContracts(): any[] {
    return this.filterBy(this.selectedContracts, 'contracts', c => c.designation);
  }

  private filterBy(items: any[], list: LinkedList, text: (item: any) => string): any[] {
    const term = this.searchTerm[list].trim().toLowerCase();
    if (!term) return items;
    return items.filter(item => (text(item) || '').toLowerCase().includes(term));
  }

  closeSearch(list: LinkedList): void {
    this.searchOpen[list] = false;
    this.searchTerm[list] = '';
  }

  focusSoon(e: any): void {
    setTimeout(() => e.component.focus());
  }

  // The search icon only shows once a list needs scrolling. Measured when the picks change,
  // not while typing, so the search doesn't vanish mid-search as filtering shrinks the list.
  scrollable: Record<LinkedList, boolean> = { projects: false, contracts: false };

  updateListsScrollable(): void {
    setTimeout(() => {
      const overflows = (el?: ElementRef<HTMLElement>) =>
        !!el && el.nativeElement.scrollHeight > el.nativeElement.clientHeight;
      this.scrollable = {
        projects: overflows(this.projectsListEl),
        contracts: overflows(this.contractsListEl),
      };
    });
  }

  removeProject(projectId: number): void {
    this.formData.projectIds = this.formData.projectIds.filter(id => id !== projectId);
    this.updateListsScrollable();
  }

  removeContract(id: number): void {
    this.formData.contractIds = this.formData.contractIds.filter(x => x !== id);
    this.updateListsScrollable();
  }

  clearArticlesContract(): void {
    this.formData.articlesContractId = null;
    this.updateOtherContracts();
  }

  // Same contract popup as on tasks: pick an existing Gesellschaftsvertrag or add a new one.
  articlesPopupVisible = false;
  get articlesPreset(): ContractFormPresetState {
    return {
      presetContractTypeKey: ARTICLES_OF_ASSOCIATION_TYPE_KEY,
      returnUrl: this.societyId ? `/contacts/society/${this.societyId}` : '/contacts/addSociety',
    };
  }

  onArticlesContractSelected(contract: any): void {
    this.formData.articlesContractId = contract.id;
    this.formData.contractIds = this.formData.contractIds.filter(id => id !== contract.id);
    this.updateOtherContracts();
    this.articlesPopupVisible = false;
  }

  // "Add Contract" leaves for "Vertrag hinzufügen"; the form comes back here after saving.
  onAddingArticlesContract(): void {
    this.societyDraft.store({ societyId: this.societyId, formData: this.formData, initialFormData: this.initialFormData });
    // Leaving on purpose: the draft is kept, so the unsaved-changes dialog would only get in the way.
    this.isSavingChanges = true;
  }

  // Document types grouped by topic; within a group sorted by the translated label. The UBO is a single file.
  readonly documentGroups: DocumentGroup[] = (<DocumentGroup[]>[
    {
      id: 'register',
      label: $localize`:@@societyDocGroupRegister:Founding & register`,
      sections: [
        { kind: 'registration_notice', label: $localize`:@@societyRegistrationNotice:Registration notice` },
        { kind: 'founding_deed', label: $localize`:@@societyFoundingDeed:Deed of incorporation` },
        { kind: 'register_extract', label: $localize`:@@societyRegisterExtract:Commercial register extract` },
        { kind: 'shareholder_list', label: $localize`:@@societyShareholderList:List of shareholders` },
        { kind: 'transparency', label: $localize`:@@societyTransparencyRegister:Transparency register` },
      ],
    },
    {
      id: 'owners',
      label: $localize`:@@societyDocGroupOwners:Owners`,
      sections: [
        { kind: 'ubo', label: $localize`:@@societyBeneficialOwners:Ultimate beneficial owner (UBO)`, single: true },
      ],
    },
    {
      id: 'bank',
      label: $localize`:@@societyDocGroupBank:Bank & payment`,
      sections: [
        { kind: 'bank_confirmation', label: $localize`:@@societyBankConfirmation:Bank confirmation of payment` },
        { kind: 'bank_power', label: $localize`:@@societyBankPowers:Bank powers of attorney` },
        { kind: 'payment_receipt', label: $localize`:@@societyPaymentReceipt:Payment receipt` },
        { kind: 'account_opening', label: $localize`:@@societyAccountOpening:Account opening documents` },
      ],
    },
    {
      id: 'accounting',
      label: $localize`:@@societyDocGroupAccounting:Accounting`,
      sections: [{ kind: 'accounting', label: $localize`:@@societyAccountingDocuments:Accounting records` }],
    },
    {
      id: 'gez',
      label: $localize`:@@societyDocGroupGez:Broadcasting fee (GEZ)`,
      sections: [
        { kind: 'gez_registration', label: $localize`:@@societyGezRegistration:GEZ registration confirmation` },
        { kind: 'gez_exemption', label: $localize`:@@societyGezExemption:GEZ exemption confirmation` },
      ],
    },
  ]).map(group => ({ ...group, sections: [...group.sections].sort((x, y) => x.label.localeCompare(y.label)) }));

  readonly selectFilesHint = $localize`:@@societySelectFiles:Select files`;
  readonly selectFileHint = $localize`:@@societySelectFile:Select file`;

  readonly documentTypeCount = this.documentGroups.reduce((sum, g) => sum + g.sections.length, 0);

  // Groups start closed; the header shows the progress.
  private openGroups = new Set<string>();

  isGroupOpen(group: DocumentGroup): boolean {
    return this.openGroups.has(group.id);
  }

  toggleGroup(group: DocumentGroup): void {
    if (this.openGroups.has(group.id)) this.openGroups.delete(group.id);
    else this.openGroups.add(group.id);
  }

  groupProgress(group: DocumentGroup): number {
    return group.sections.filter(s => this.documentsOf(s.kind).length).length;
  }

  get documentTotalText(): string {
    const present = this.documentGroups.reduce((sum, g) => sum + this.groupProgress(g), 0);
    const total = this.documentTypeCount;
    return $localize`:@@societyDocumentsPresentCount:${present}:present: of ${total}:total: present`;
  }

  documentsOf(kind: SocietyDocumentKind): SocietyFile[] {
    return this.formData.documents.filter(d => d.kind === kind);
  }

  get beneficialOwnerDocument(): SocietyFile | null {
    return this.formData.documents.find(d => d.kind === 'ubo') ?? null;
  }

  // Picked files are uploaded on save, after the Gesellschaft itself.
  addFiles(kind: SocietyDocumentKind, input: HTMLInputElement): void {
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (!files.length) return;
    // The UBO slot holds one file: a new one replaces the old.
    const picked = kind === 'ubo' ? files.slice(0, 1) : files;
    if (kind === 'ubo') this.releaseBeneficialOwnerSlot();
    this.formData.documents = [...this.formData.documents, ...picked.map(file => ({ kind, name: file.name, file }))];
  }

  // The UBO slot holds one file: the current one goes, remembered when saved (see handleSubmit).
  private releaseBeneficialOwnerSlot(): void {
    const replaced = this.beneficialOwnerDocument;
    if (!replaced) return;
    if (replaced.saved) this.formData.replacedUboId = replaced.saved.id;
    this.removeFile(replaced);
  }

  removeFile(doc: SocietyFile): void {
    if (doc.saved) this.formData.removedDocumentIds = [...this.formData.removedDocumentIds, doc.saved.id];
    this.formData.documents = this.formData.documents.filter(d => d !== doc);
  }

  // Same viewer as every other document; a file not uploaded yet opens from the browser.
  openFile(doc: SocietyFile): void {
    if (doc.file) this.fileViewerOverlay.openLocalFile(doc.file);
    else if (doc.saved) this.fileViewerService.openOrDownloadFile('society-file', doc.saved.uuid, doc.name, '');
  }

  private serialize(): string {
    const { documents, ...rest } = this.formData;
    return JSON.stringify({
      ...rest,
      documents: documents.map(d => d.saved?.id ?? d.name),
    });
  }

  checkHasChanges(): void {
    this.hasChanges = this.initialFormData != null && this.serialize() !== this.initialFormData;
  }

  saveUnsavedChanges(targetUrl?: string): Observable<boolean> {
    this.handleSubmit(targetUrl);
    return of(false);
  }

  private toPayload(): SocietyItem {
    const f = this.formData;
    // Null-safe: a draft kept from before a field existed doesn't have it yet.
    const contact = (c: ContactRef | undefined) => (c?.contactId ? { contactId: c.contactId, contactType: c.contactType } : null);
    const date = f.foundingDate;
    return {
      id: this.societyId,
      legalName: f.legalName,
      legalForm: f.legalForm,
      status: f.status,
      transactionType: f.transactionType,
      foundingDate: date
        ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
        : null,
      registeredSeatAddress: f.registeredSeatAddress,
      registeredSeatZipcode: f.registeredSeatZipcode,
      registeredSeat: f.registeredSeat,
      registeredSeatCountryId: f.registeredSeatCountryId,
      businessAddressDiffers: f.businessAddressDiffers,
      // The list and the org chart show the business address: without its own, the Satzungssitz.
      address: f.businessAddressDiffers ? f.address : f.registeredSeatAddress,
      zipcode: f.businessAddressDiffers ? f.zipcode : f.registeredSeatZipcode,
      city: f.businessAddressDiffers ? f.city : f.registeredSeat,
      countryId: f.businessAddressDiffers ? f.countryId : f.registeredSeatCountryId,
      registerCourt: f.registerCourt,
      registerNumber: f.registerNumber,
      // A KG has no Stammkapital: a value left over from another legal form is not saved.
      shareCapital: this.hasPartnerRoles ? null : f.shareCapital,
      purpose: f.purpose,
      representationRule: f.representationRule,
      vatLiable: f.vatLiable,
      taxNumber: f.taxNumber,
      vatId: f.vatId,
      taxCare: f.taxCare,
      salesModelId: f.salesModelId,
      taxAdvisor: contact(f.taxAdvisor),
      // Only asked for on a purchase: a value left over from switching away is not saved.
      previousTaxAdvisor: this.isPurchase ? contact(f.previousTaxAdvisor) : null,
      accountant: contact(f.accountant),
      accountantDeputy: contact(f.accountantDeputy),
      articlesContractId: f.articlesContractId,
      // A role left over from switching to another legal form is not saved.
      shareholders: f.shareholders
        .filter(sh => sh.contactId)
        .map(sh => {
          const role = this.hasPartnerRoles ? sh.role : null;
          return { ...sh, role, liabilityAmount: role === 'limited_partner' ? sh.liabilityAmount : null };
        }),
      managingDirectors: f.managingDirectors.filter(d => d.contactId),
      projectIds: f.projectIds,
      contractIds: f.contractIds,
    };
  }

  private saving = false;

  /** targetUrl: where the unsaved-changes dialog was heading; otherwise back to the list. */
  async handleSubmit(targetUrl?: string): Promise<void> {
    if (this.saving) return;
    const validation = this.societyValidationGroup.instance.validate();
    if (!validation.isValid) {
      this.validationToast.showToast($localize`Please fill in all fields before saving`);
      return;
    }

    this.saving = true;
    try {
      // A replaced UBO file is retired by the server when its successor uploads; removing it in the
      // save would leave no UBO file at all if that upload then fails.
      const uboPending = this.formData.documents.some(d => d.kind === 'ubo' && d.file);
      const removedIds = uboPending
        ? this.formData.removedDocumentIds.filter(id => id !== this.formData.replacedUboId)
        : this.formData.removedDocumentIds;
      const result = await this.societyService
        .saveSociety(this.toPayload(), removedIds)
        .catch(error => ({ hasError: true, feedback: error, societyId: undefined }));
      if (result?.hasError || !result?.societyId) {
        AppGlobals.ShowErrorMessage(result?.feedback);
        return;
      }

      // One file per call, as for contract documents. A failed upload doesn't undo the save.
      const failed: SocietyFile[] = [];
      for (const doc of this.formData.documents.filter(d => d.file)) {
        const upload = await this.societyService
          .uploadSocietyFile(result.societyId, doc.kind, doc.file!)
          .catch(error => ({ hasError: true, feedback: error }));
        if (upload?.hasError) {
          AppGlobals.ShowErrorMessage(upload.feedback);
          failed.push(doc);
        }
      }

      // New or renamed: the contacts dropdowns of this tab list it right away.
      this.societyService.loadContacts(true);

      // Saved, but some files weren't: stay on the saved Gesellschaft with those files still
      // picked, so saving again retries them.
      if (failed.length) {
        if (!this.societyId) {
          this.societyId = result.societyId;
          this.location.replaceState(`/contacts/society/${result.societyId}`);
        }
        await this.loadSociety();
        if (failed.some(d => d.kind === 'ubo')) this.releaseBeneficialOwnerSlot();
        this.formData.documents = [...this.formData.documents, ...failed];
        this.checkHasChanges();
        return;
      }
      this.hasChanges = false;
      this.isSavingChanges = true;
      this.router.navigateByUrl(targetUrl ?? '/contacts/societies');
    } finally {
      this.saving = false;
    }
  }

  handleAbort(): void {
    this.router.navigate(['contacts/societies']);
  }
}
